import { useCallback, useEffect, useMemo } from 'react';

import {
  useCreateConsumerFamilyChild,
  useShowConsumerFamily,
  useUpdateConsumerFamilyChild,
} from '@givebacks/api/backer';
import { useForm, useFormyValidator } from '@givebacks/formy';
import { Button, Grid, Modal, Stack, Text, TextInput, notifications } from 'ui';
import { UseFormReturnType, isNotEmpty } from 'ui/Form';

import { getErrorMessage_v2 } from '../../../../../../utils/errorMessage';
import {
  ModalTitleWithBackButton,
  useDirectorySettingsModalProvider,
} from '../../DirectorySettingsModalProvider';
import { ChildOrganizationSearchableMultiselect } from './ChildOrganizationSearchableMultiselect';
import { ChildProfileForm } from './types';

const AddEditChildModalFooter = () => {
  const { openBack, meta, openModalById } = useDirectorySettingsModalProvider({});
  const { mutate: createConsumerFamilyChild } = useCreateConsumerFamilyChild();
  const { mutate: updateConsumerFamilyChild } = useUpdateConsumerFamilyChild();

  const { useGetForm } = useFormyValidator<{
    childProfile: UseFormReturnType<ChildProfileForm>;
  }>();
  const form = useGetForm('childProfile');

  const handleAddChild = useCallback(() => {
    if (!meta.familyId) return;
    if (!form?.validate().hasErrors) {
      createConsumerFamilyChild(
        {
          familyId: meta.familyId,
          child: {
            first_name: form.values.firstName,
            last_name: form.values.lastName,
            cause_ids: form.values.organizationIds,
          },
        },
        {
          onSuccess: () => {
            notifications.show({
              message: `${form.values.firstName} ${form.values.lastName} added to family`,
              variant: 'success',
            });
            openBack();
          },
          onError: (error) => {
            notifications.show({
              message: getErrorMessage_v2(error),
              variant: 'error',
            });
          },
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form?.values?.firstName,
    form?.values?.lastName,
    form?.values?.organizationIds,
    meta.familyId,
    createConsumerFamilyChild,
    openBack,
  ]);

  const handleSave = useCallback(() => {
    if (!form?.validate().hasErrors) {
      if (!meta.familyId || !meta.childId) return;
      updateConsumerFamilyChild(
        {
          familyId: meta.familyId,
          childId: meta.childId,
          child: {
            first_name: form.values.firstName,
            last_name: form.values.lastName,
            cause_ids: form.values.organizationIds,
          },
        },
        {
          onSuccess: () => {
            notifications.show({
              message: `${form.values.firstName} ${form.values.lastName} saved`,
              variant: 'success',
            });
            openBack();
          },
          onError: (error) => {
            notifications.show({
              message: getErrorMessage_v2(error),
              variant: 'error',
            });
          },
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form?.values?.firstName,
    form?.values?.lastName,
    form?.values?.organizationIds,
    meta.familyId,
    meta.childId,
    updateConsumerFamilyChild,
    openBack,
  ]);

  return (
    <Modal.AffixedFooter>
      {meta.childId ? (
        <Stack gap="sm">
          <Button fullWidth onClick={handleSave}>
            Save Changes
          </Button>

          <Button
            color="cerise"
            variant="outline"
            fullWidth
            onClick={() => openModalById('remove-child')}
          >
            Remove Child
          </Button>
        </Stack>
      ) : (
        <Button fullWidth onClick={handleAddChild}>
          Add Child
        </Button>
      )}
    </Modal.AffixedFooter>
  );
};

const AddEditChildForm = () => {
  const { meta } = useDirectorySettingsModalProvider({});

  const { data: child } = useShowConsumerFamily({
    familyId: meta.familyId,
    include: 'children',
    select: (data) => data.children?.find((child) => child.uuid === meta.childId),
  });

  const form = useForm<ChildProfileForm>(
    {
      initialValues: {
        firstName: '',
        lastName: '',
        organizationIds: [],
      },
      validate: {
        firstName: isNotEmpty('First name is required'),
        lastName: isNotEmpty('Last name is required'),
      },
    },
    { id: 'childProfile' }
  );

  useEffect(() => {
    if (child) {
      form.initialize({
        firstName: child.first_name,
        lastName: child.last_name,
        organizationIds: child.cause_ids,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [child]);

  return (
    <Stack gap="lg">
      <Text fz="xs" c="gray">
        {meta.childId
          ? 'The changes you make will be reflected in your organizations’ directories.'
          : 'Add a child and the organizations they are part of if you’d like them displayed in those organizations’ directories.'}
      </Text>
      <Grid>
        <Grid.Col span={6}>
          <TextInput label="First Name" {...form.getInputProps('firstName')} required />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput label="Last Name" {...form.getInputProps('lastName')} required />
        </Grid.Col>
        <Grid.Col span={12}>
          <ChildOrganizationSearchableMultiselect form={form} />
        </Grid.Col>
      </Grid>
    </Stack>
  );
};

const AddEditChildModal = () => {
  const { opened, close, meta } = useDirectorySettingsModalProvider({
    id: 'child-profile',
  });

  const title = useMemo(() => {
    return meta.childId ? 'Edit Profile' : 'Add Child';
  }, [meta.childId]);

  return (
    <Modal
      opened={opened}
      onClose={close}
      title={<ModalTitleWithBackButton>{title}</ModalTitleWithBackButton>}
    >
      <AddEditChildForm />
      <AddEditChildModalFooter />
    </Modal>
  );
};

export { AddEditChildModal };
